import { Container } from 'react-bootstrap';
import React, { useContext, useRef } from 'react';
import { exportImage } from '@progress/kendo-drawing';
import { Button } from '@progress/kendo-react-buttons';
import { QRCode } from '@progress/kendo-react-barcodes';
import { NavLink, useNavigate, useParams } from 'react-router-dom';

import {
  NotificationContent,
  NotificationContext
} from '../../contexts/notification';
import { ArGameNames } from '../../constants/argame';
import HelpSupport from '../../components/help-support';
import { useAssessment } from '../../hooks/use-assessment';
import { GameDocumentContext } from '../../contexts/game-document';
import { GameControl } from '../../components/facilitator/game-control';
import { QRTaskList } from '../../components/facilitator/task/qr';

const TitleWithUrl: React.FC<{ title: string; url: string; qr: string }> = ({
  qr,
  url,
  title
}) => {
  const [, setNotification] = useContext(NotificationContext);
  const qrRef = useRef<QRCode>(null);

  const notificationHandler = (message: string) => {
    const responseTeamNotification: NotificationContent = {
      icon: 'notifications',
      isHide: false,
      message: (
        <span>
          <strong>{message}</strong>
        </span>
      )
    };
    setNotification((prevNotification) => ({
      ...prevNotification,
      content: [...prevNotification.content, responseTeamNotification]
    }));
  };

  const handleDownloadQr = () => {
    if (qrRef.current) {
      const visual = qrRef.current.exportVisual();
      exportImage(visual)
        .then((dataUri) => {
          const link = document.createElement('a');
          let cleanTitle = title.replace(/qr\s*code/i, '').trim();
          cleanTitle = cleanTitle.replace(/[\+\-]*$/, '').trim();
          const fileNamePrefix = cleanTitle
            .replace(/[^a-zA-Z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .toLowerCase();

          link.download = `${fileNamePrefix ? fileNamePrefix + '-' : ''}qrcode.png`;
          link.href = dataUri;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          notificationHandler('QR Code downloaded!');
        })
        .catch((err) => {
          console.error('Failed to download QR Code', err);
        });
    }
  };

  return (
    <div className={'d-flex flex-column gap-2'}>
      <span
        className={'text-muted'}
        style={{
          fontSize: '14px'
        }}>
        {title}
      </span>
      <div className={'d-flex align-items-center gap-1'}>
        <NavLink
          to={url}
          className={
            'd-flex align-items-center text-primary text-decoration-none'
          }
          target={'_blank'}>
          <u>{url}</u>
          <span
            className={'material-symbols-outlined p-0 m-0'}
            style={{
              fontSize: '16px'
            }}>
            open_in_new
          </span>
        </NavLink>
        <span
          onClick={() => {
            navigator.clipboard.writeText(url);
            notificationHandler('URL copied!');
          }}
          className={
            'material-symbols-outlined cursor-pointer text-primary p-0 m-0'
          }>
          content_copy
        </span>
        <NavLink
          to={qr}
          className={
            'd-flex align-items-center text-primary text-decoration-none'
          }
          target="_blank">
          <span
            className={
              'material-symbols-outlined cursor-pointer text-primary p-0 m-0'
            }>
            qr_code_scanner
          </span>
        </NavLink>
        <span
          onClick={handleDownloadQr}
          title="Download QR Code"
          className={
            'material-symbols-outlined cursor-pointer text-primary p-0 m-0'
          }>
          download
        </span>
        <div
          style={{ position: 'absolute', left: '-10000px', top: '-10000px' }}>
          <div style={{ background: 'white', padding: '16px' }}>
            <QRCode ref={qrRef} size={350} value={url} errorCorrection="M" />
          </div>
        </div>
      </div>
    </div>
  );
};

export const DashboardPage = () => {
  const { gameCode } = useParams();
  const navigate = useNavigate();
  const [gameDocument] = useContext(GameDocumentContext);
  const assessment = useAssessment(gameCode!, true);

  const isARPyramidGame = ArGameNames.includes(
    gameDocument.gameDocument?.name!
  );
  const assessmmentStatus = assessment.data?.assessmentStatus;
  const assessmmentStatusClass =
    assessment.data?.assessmentStatus === 'Running'
      ? 'text-success'
      : 'text-muted';

  return (
    <>
      <Container className={'overflow-auto'} fluid>
        <HelpSupport
          title="Getting ready"
          url="https://forum.catalystglobal.com/t/4643"
        />
        <GameControl />

        <div className={`d-flex flex-column gap-3 py-3`}>
          <span className={'fw-bold text-black'}>Player Access</span>
          <TitleWithUrl
            title={'Participant Link + QR Code'}
            url={
              isARPyramidGame
                ? `arpyramid://catalystglobal?${window.location.origin}?${gameCode}`
                : `${window.location.origin}/games/${gameCode}`
            }
            qr={`${window.location.origin}/games/${gameCode}/facilitator/game`}
          />
          {!isARPyramidGame && (
            <TitleWithUrl
              title={'Game - Pre Load Link + QR Code'}
              url={`${window.location.origin}/preload/${gameCode}`}
              qr={`${window.location.origin}/games/${gameCode}/facilitator/preload`}
            />
          )}
        </div>

        <div className={'py-3 border-bottom'}>
          <QRTaskList />
        </div>

        {assessment.data && (
          <div
            className={
              'd-flex align-items-center justify-content-between py-3 border-bottom'
            }>
            <TitleWithUrl
              title={'Assessment - Facilitator Link + QR Code'}
              url={`${window.location.origin}/facilitator/assessment/${gameCode}`}
              qr={`${window.location.origin}/games/${gameCode}/facilitator/assessment-facilitator`}
            />

            <span className={assessmmentStatusClass}>{assessmmentStatus}</span>
          </div>
        )}

        <div
          className={'d-flex flex-column py-3 gap-2 border-bottom'}
          style={{
            fontSize: '14px'
          }}>
          <span
            className={'fw-bold text-black'}
            style={{
              fontSize: '16px'
            }}>
            Game Setup
          </span>
          <div className={'d-flex flex-column gap-1'}>
            <NavLink
              to={`/games/${gameCode}/facilitator/teams`}
              className={'fw-bold text-black'}>
              Create teams
            </NavLink>
            <p>
              Players must join a team setting is turned on. Create teams before
              you start the game. Once you have created teams, players can
              select a team when they join.
            </p>
          </div>
          <div className={'d-flex flex-column gap-1'}>
            <span className={'fw-bold'}>Invite players to join the game</span>
            <p>
              Ask participants to scan the QR code or open the link. To join
              players, enter a name and select an Avatar and language. Players
              enter the lobby. The ‘Enter the Game’ button is inactive until the
              game starts.
            </p>
          </div>
          <div className={'d-flex flex-column gap-1'}>
            <NavLink
              to={`/games/${gameCode}/facilitator/players`}
              className={'fw-bold text-black'}>
              Add players to teams
            </NavLink>
            <p>Ensure all players have joined a team.</p>
          </div>
          <div className={'d-flex flex-column gap-1'}>
            <span className={'fw-bold'}>Start the game </span>
            <p>
              When all players have joined the game, click the Start button in
              the top right. Once started, the game can be paused at any time
              and restarted.
            </p>
          </div>
          <div className={'d-flex flex-column gap-1'}>
            <NavLink
              to={`/games/${gameCode}/facilitator/settings`}
              className={'fw-bold text-black'}>
              Change the state settings
            </NavLink>
            <p>
              State settings enable you to change the game settings while the
              event is running without altering the settings of the game
              document and creating a new event. They can only be made after the
              game has started.
            </p>
          </div>
        </div>

        <div className={'py-3'}>
          <div
            className={'d-flex flex-column align-items-center'}
            style={{
              gap: '10px',
              backgroundColor: '#F8FCFE',
              border: '1px solid #A5B7BF',
              borderRadius: '8px',
              padding: '16px 24px'
            }}>
            <span
              className="material-symbols-outlined"
              style={{
                color: '#4698CB',
                fontSize: '55px'
              }}>
              books_movies_and_music
            </span>
            <span
              style={{
                color: '#003A64',
                fontSize: '21px',
                fontWeight: 'bold'
              }}>
              Create re-cap short
            </span>
            <span
              style={{
                color: '#003A64'
              }}>
              Generate a short video recap using images and videos from teams
              answers.
            </span>
            <Button
              themeColor={'primary'}
              onClick={() => navigate(`/games/${gameCode}/facilitator/reels`)}>
              Get started
            </Button>
          </div>
        </div>
      </Container>
    </>
  );
};
